import { useState } from "react";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Menu,
    MenuItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { Add, Delete, Edit, MoreVert, ToggleOff, ToggleOn } from "@mui/icons-material";
import { makeStyles } from "@/compat/muiStyles";
import { useSnackbar } from "notistack";
import { useAppSelector } from "@/redux/hooks";
import CardBase from "../../../components/cards/CardBase";
import useContactsForAppAndSite from "../../../hooks/useContactsForAppAndSite";
import useShareGroups from "../../hooks/queries/useShareGroups";
import useSaveShareGroup from "../../hooks/mutations/useSaveShareGroup";
import useDeleteShareGroup from "../../hooks/mutations/useDeleteShareGroup";
import ShareGroupEditorDialog from "./ShareGroupEditorDialog";
import type { SaveShareGroupRequest, ShareGroupDTO } from "../../types/shareGroupDtos";

const useStyles = makeStyles((theme) => ({
    empty: { color: theme.palette.text.secondary, padding: theme.spacing(3), textAlign: "center" },
    showInactive: { marginBottom: theme.spacing(1) },
    menuItem: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        display: "flex",
        gap: theme.spacing(1),
    },
}));

export default function ShareGroupsTableCard() {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { sites, selectedAccount, appSiteIds, activeAppId } = useAppSelector((state: any) => state.account);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editing, setEditing] = useState<ShareGroupDTO | null>(null);
    const [deleting, setDeleting] = useState<ShareGroupDTO | null>(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openMenu, setOpenMenu] = useState<number | false>(false);
    const [showInactive, setShowInactive] = useState(false);

    const handleClick = (event, id) => {
        setAnchorEl(event.currentTarget);
        setOpenMenu(id);
    };
    const handleClose = () => {
        setAnchorEl(null);
        setOpenMenu(false);
    };

    const accountExternalId = selectedAccount?.account?.externalId;
    const siteOptions = Object.values(sites ?? {});

    const contacts = useContactsForAppAndSite({ selectedSiteId: null });

    const shareGroups = useShareGroups({
        accountExternalId,
        siteExternalIds: appSiteIds?.[activeAppId] ?? [],
    });
    const saveShareGroup = useSaveShareGroup();
    const deleteShareGroup = useDeleteShareGroup();

    const groups: ShareGroupDTO[] = (Array.isArray(shareGroups?.data) ? shareGroups.data : []).filter((g) =>
        showInactive ? !g.isActive : g.isActive
    );

    const refresh = () => shareGroups.refetch?.();

    const openCreate = () => {
        setEditing(null);
        setEditorOpen(true);
    };

    const openEdit = (group: ShareGroupDTO) => {
        setEditing(group);
        setEditorOpen(true);
    };

    const save = (group: SaveShareGroupRequest) =>
        saveShareGroup.mutate(group, {
            onSuccess: () => {
                setEditorOpen(false);
                refresh();
                enqueueSnackbar(group.id ? "Group updated" : "Group created", { variant: "default" });
            },
            onError: () => enqueueSnackbar("Could not save the group. Please try again.", { variant: "error" }),
        });

    const toggleActive = (group: ShareGroupDTO) =>
        saveShareGroup.mutate(
            { ...group, isActive: !group.isActive },
            {
                onSuccess: () => refresh(),
                onError: () => enqueueSnackbar("Could not update the group. Please try again.", { variant: "error" }),
            }
        );

    const confirmDelete = () => {
        if (!deleting) return;
        deleteShareGroup.mutate(deleting.id, {
            onSuccess: () => {
                setDeleting(null);
                refresh();
                enqueueSnackbar("Group deleted", { variant: "default" });
            },
            onError: () => enqueueSnackbar("Could not delete the group. Please try again.", { variant: "error" }),
        });
    };

    const visibilityLabel = (group: ShareGroupDTO) =>
        !group.siteExternalId ? "All sites" : sites?.[group.siteExternalId]?.name ?? "Unknown site";

    return (
        <CardBase
            title="Share groups"
            description="Reusable sets of recipients that can be selected when sharing a risk assessment."
            isLoading={shareGroups?.isLoading}
            isFetching={shareGroups?.isFetching}
            rightComponent={
                <Button variant="contained" color="primary" startIcon={<Add />} onClick={openCreate}>
                    Create group
                </Button>
            }
        >
            <FormControlLabel
                className={classes.showInactive}
                control={
                    <Switch
                        size="small"
                        color="secondary"
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                    />
                }
                label="Show Inactive"
            />

            {groups.length === 0 ? (
                <Typography className={classes.empty}>
                    {showInactive
                        ? "No inactive groups."
                        : "No share groups yet. Create one to save a set of recipients you share with often."}
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Available for</TableCell>
                                <TableCell>Members</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groups.map((group) => (
                                <TableRow key={group.id}>
                                    <TableCell>{group.name}</TableCell>
                                    <TableCell>
                                        <Chip size="small" variant="outlined" label={visibilityLabel(group)} />
                                    </TableCell>
                                    <TableCell>{group.members?.length ?? 0}</TableCell>
                                    <TableCell>
                                        <Tooltip title="More">
                                            <IconButton
                                                aria-label="more"
                                                aria-controls="share-group-menu"
                                                aria-haspopup="true"
                                                onClick={(e) => handleClick(e, group.id)}
                                            >
                                                <MoreVert />
                                            </IconButton>
                                        </Tooltip>
                                        <Menu
                                            id="share-group-menu"
                                            anchorEl={anchorEl}
                                            keepMounted
                                            open={openMenu === group.id}
                                            onClose={handleClose}
                                        >
                                            <MenuItem
                                                className={classes.menuItem}
                                                onClick={() => {
                                                    handleClose();
                                                    openEdit(group);
                                                }}
                                            >
                                                <Edit />
                                                Edit Group
                                            </MenuItem>
                                            <MenuItem
                                                className={classes.menuItem}
                                                onClick={() => {
                                                    handleClose();
                                                    toggleActive(group);
                                                }}
                                            >
                                                {group.isActive ? <ToggleOff /> : <ToggleOn />}
                                                {group.isActive ? "Deactivate" : "Activate"} Group
                                            </MenuItem>
                                            <MenuItem
                                                className={classes.menuItem}
                                                onClick={() => {
                                                    handleClose();
                                                    setDeleting(group);
                                                }}
                                            >
                                                <Delete />
                                                Delete Group
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ShareGroupEditorDialog
                open={editorOpen}
                group={editing}
                contacts={contacts?.data ?? {}}
                sites={siteOptions}
                accountExternalId={accountExternalId}
                isSaving={Boolean(saveShareGroup.isLoading)}
                onClose={() => setEditorOpen(false)}
                onSave={save}
            />

            <Dialog open={!!deleting} onClose={() => setDeleting(null)}>
                <DialogTitle>Delete share group?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete &ldquo;{deleting?.name}&rdquo;? Risk assessments already shared with
                        these people are not affected.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleting(null)}>Cancel</Button>
                    <Button onClick={confirmDelete} variant="contained" color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </CardBase>
    );
}
